import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withKeys as _withKeys, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onKeydown"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", {
  class: "h-1",
  style: {"background":"linear-gradient(to right, #2f7f91, #3ca2b9)"}
}, null, -1)
const _hoisted_3 = { class: "flex flex-col items-center px-4 pt-4 pb-3" }
const _hoisted_4 = ["cx", "cy", "stroke"]
const _hoisted_5 = ["cx", "cy", "stroke", "stroke-dashoffset"]
const _hoisted_6 = { class: "text-lg font-bold" }
const _hoisted_7 = ["title"]
const _hoisted_8 = ["title"]
const _hoisted_9 = { class: "border-t border-gray-100 px-4 py-2.5 flex flex-col gap-1.5" }
const _hoisted_10 = { class: "flex items-center gap-2 text-xs text-gray-600" }
const _hoisted_11 = { class: "font-medium" }
const _hoisted_12 = { class: "flex items-center gap-2 text-xs text-gray-600" }
const _hoisted_13 = { class: "truncate capitalize font-medium" }
const _hoisted_14 = {
  key: 0,
  class: "border-t border-gray-100 px-4 py-2"
}
const _hoisted_15 = { class: "absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" }

import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { mdiCardAccountDetailsOutline, mdiOfficeBuildingOutline, mdiChevronRight } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import type { DoctorGroupedResponse } from '@/Network/Types/Responses/CompanyResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import {
  getInviteStatusConfig,
  getInitials,
  resolveAvatarUrl,
  toTitleCase
} from '../../helpers/doctorStatus'

const AVATAR_SIZE = 48
const STROKE_WIDTH = 4

export default /*#__PURE__*/_defineComponent({
  __name: 'Card',
  props: {
    doctor: {}
  },
  setup(__props: any) {

const props = __props;



const router = useRouter()

const navigateToDetail = () => {
  router.push({ name: 'doctor-detail', params: { doctorId: props.doctor.doctor.id } })
}

const doctorName = computed(() =>
  toTitleCase(props.doctor.doctor.user?.name || props.doctor.doctor.mainCrm.name)
)

const specialtyLabel = computed(() => {
  const list = props.doctor.doctor.specialties.map(toTitleCase)
  return list.length ? list.join(', ') : 'Sem especialidade'
})

const avatarSrc = computed(() => resolveAvatarUrl(props.doctor.doctor.user?.avatarUrl))

const initials = computed(() => getInitials(doctorName.value))

const radius = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const svgSize = radius * 2 + STROKE_WIDTH
const circumference = 2 * Math.PI * radius

const clampedScore = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return 0
  return Math.min(100, Math.max(0, (score + 100) / 2))
})

const offset = computed(() => circumference * (1 - clampedScore.value / 100))

const arcColor = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
})

const crmDisplay = computed(
  () => `${props.doctor.doctor.mainCrm.number}-${props.doctor.doctor.mainCrm.uf}`
)

const companyLabel = computed(() => {
  const companies = props.doctor.companies
  if (companies.length === 0) return 'Sem vínculo'
  if (companies.length === 1) return companies[0].company.name.toLowerCase()
  return `${companies.length} empresas`
})

const inviteStatus = computed(() => getInviteStatusConfig(props.doctor.companies[0]?.doctorStatus))

const isPending = computed(
  () => props.doctor.companies[0]?.doctorStatus === CompanyDoctorInviteStatusEnum.PENDING
)

return (_ctx: any,_cache: any) => {
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", {
    "data-testid": "doctor-card-button",
    role: "button",
    tabindex: "0",
    class: "group relative rounded-2xl border border-gray-200 bg-white overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary20 hover:-translate-y-0.5",
    onClick: navigateToDetail,
    onKeydown: [
      _withKeys(navigateToDetail, ["enter"]),
      _withKeys(_withModifiers(navigateToDetail, ["prevent"]), ["space"])
    ]
  }, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        class: "relative shrink-0",
        style: _normalizeStyle({ width: svgSize + 'px', height: svgSize + 'px' })
      }, [
        (_openBlock(), _createElementBlock("svg", {
          width: svgSize,
          height: svgSize,
          class: "absolute top-0 left-0 -rotate-90",
          "aria-hidden": "true"
        }, [
          _createElementVNode("circle", {
            cx: svgSize / 2,
            cy: svgSize / 2,
            r: radius,
            stroke: _unref(NEUTRAL_RING_COLOR),
            "stroke-width": STROKE_WIDTH,
            fill: "none"
          }, null, 8, _hoisted_4),
          (clampedScore.value > 0)
            ? (_openBlock(), _createElementBlock("circle", {
                key: 0,
                cx: svgSize / 2,
                cy: svgSize / 2,
                r: radius,
                stroke: arcColor.value,
                "stroke-width": STROKE_WIDTH,
                fill: "none",
                "stroke-dasharray": circumference,
                "stroke-dashoffset": offset.value,
                "stroke-linecap": "round"
              }, null, 8, _hoisted_5))
            : _createCommentVNode("", true)
        ])),
        _createVNode(_component_v_avatar, {
          color: "primary",
          size: AVATAR_SIZE,
          class: "absolute",
          style: _normalizeStyle({ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' })
        }, {
          default: _withCtx(() => [
            _createVNode(ProtectedAvatar, {
              src: avatarSrc.value,
              size: AVATAR_SIZE
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_6, _toDisplayString(initials.value), 1)
              ]),
              _: 1
            }, 8, ["src"])
          ]),
          _: 1
        }, 8, ["style"])
      ], 4),
      _createElementVNode("p", {
        class: "mt-2 text-sm font-semibold text-gray-900 text-center truncate w-full",
        title: doctorName.value
      }, _toDisplayString(doctorName.value), 9, _hoisted_7),
      _createElementVNode("p", {
        class: "mt-0.5 text-xs text-gray-500 text-center truncate w-full",
        title: specialtyLabel.value
      }, _toDisplayString(specialtyLabel.value), 9, _hoisted_8)
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _createVNode(_component_v_icon, {
          size: "14",
          color: "primary30"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(mdiCardAccountDetailsOutline)), 1)
          ]),
          _: 1
        }),
        _createElementVNode("span", _hoisted_11, "CRM " + _toDisplayString(crmDisplay.value), 1)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createVNode(_component_v_icon, {
          size: "14",
          color: "primary30"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(mdiOfficeBuildingOutline)), 1)
          ]),
          _: 1
        }),
        _createElementVNode("span", _hoisted_13, _toDisplayString(companyLabel.value), 1)
      ])
    ]),
    (isPending.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createVNode(LabelBadge, {
            label: inviteStatus.value!.label,
            color: inviteStatus.value!.color,
            icon: null,
            size: "small"
          }, null, 8, ["label", "color"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_15, [
      _createVNode(_component_v_icon, {
        size: "16",
        color: "grey-lighten-1"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(mdiChevronRight)), 1)
        ]),
        _: 1
      })
    ])
  ], 40, _hoisted_1))
}
}

})